import { handleApiError } from "../../error"
import { ExternalAppId } from "../../generated/enums"
import { omniaResolve } from "../../promises"
import { IOmniaShell } from "../../shell"
import { IUibModalScope } from "../../types"
import { defineDirective } from "../../utils/angular"
import { IAppService } from "../types"
import { confirmDisconnect, confirmSyncReset } from "./msg"
import { ConnectionVM } from "./vm"

/** @internal */
export class ManageCtrl {

    static create(): ng.IDirective {

        return defineDirective(ManageCtrl, "shared/apps/manage/manage.html", {});

    }

    private $scope: IUibModalScope;

    public connections: ConnectionVM[] = []
    closed = false

    static $inject = ["$scope", "$q", "OmniaShell", "OmniaApps"]

    constructor(
        $scope: ng.IScope,
        $q: ng.IQService,
        private shell: IOmniaShell,
        appSvc: IAppService,
        private session = shell.getSession()) {

        this.$scope = <IUibModalScope>$scope.$parent;

        const { holding } = this.session

        const apps = appSvc.getSupportedApps()

        this.connections = apps.map(def => {
            const session = appSvc.createSession(def)
            return new ConnectionVM(def, holding, session)
        })

        this.shell.disableBlockUI(

            () => appSvc.refresh(true).then(() => $q.all(this.connections.map(cn => cn.setup())))

        )

    }

    disconnect(connection: ConnectionVM) {

        const confirm = confirmDisconnect(connection, this.shell)
            .then(ok => ok ? confirmSyncReset(connection, this.shell, true) : false)

        return confirm.then(r => {

            if (r) {
                connection.session.disconnect(r !== "keep").catch(e => handleApiError(this.shell, e))
                return true
            }
        })

    }

    canDisconnect(cn: ConnectionVM) {
        return cn.connected && cn.canUpdate() && (!cn.canSaveConfig())
    }

    private refreshOnConnection(connection: ConnectionVM) {

        if (connection.connected && connection.valid && connection.provider.orgs) {
            return connection.refreshConfig()
        }


    }

    reconnect(connection: ConnectionVM) {

        return this.connect(connection)

    }

    connect(connection: ConnectionVM) {

        this.shell
            .showComponentAsDialog(
                "omnia-apps-connect-popup", {
                session: connection.session,
            }, {
                backdrop: 'static'
            })
            .then(() => this.refreshOnConnection(connection))

    }

    saveConfig(connection: ConnectionVM) {

        const confirm = connection.organisationChanged
            ? confirmSyncReset(connection, this.shell, false).then(r => !!r)
            : omniaResolve(true)

        return confirm.then(ok => {
            if (ok) {
                connection.saveConfig()
                return true
            }
        })

    }

    hasNoOrgs(cn: ConnectionVM) {

        return cn.connected
            && cn.valid
            && cn.ready
            && cn.hasConfig()
            && (!cn.hasOrganisations)
            && cn.orgRequired
            && cn.canUpdate()

    }

    canConfig(cn: ConnectionVM) {

        return cn.connected
            && cn.valid
            && cn.hasConfig()

    }

    isInvalid(cn: ConnectionVM) {
        return cn.connected && (!cn.valid)
    }

    close() {
        this.closed = true
        this.$scope.$close()
    }

    getEmptyOrgListMessage(cn: ConnectionVM) {
        return emptyOrgListMessage[cn.provider.appId] ?? emptyOrgListMessage.default
    }
}

const emptyOrgListMessage: Partial<Record<ExternalAppId | "default", string>> = {
    default: "No sites found - please add a site to your external account and try again",
    [ExternalAppId.Glgm]: "No sites found - please add a site to GLGM and try again",
    // 210622 - Organizations not supported by Climate
    // [ExternalAppId.FieldView]: "Your Climate farms are not available yet - please check again later."
}
